import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Chip, 
  Divider,
  Button,
  useTheme,
  alpha,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  Schedule,
  Star,
  People,
  RoomService,
  Edit,
  Share,
  Business,
  Assessment,
  CalendarMonth
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { FaInstagram, FaFacebook, FaWhatsapp, FaTelegram, FaLinkedin } from 'react-icons/fa';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useQuery } from '@apollo/client';
import { GET_ALL_STAFF, GET_ALL_SERVICES, GET_ALL_RATINGS, GET_STORE_BY_ID } from '../graphql/queries';
import LoadingSpinner from '../components/common/LoadingSpinner';
import CustomSnackbar, { SnackbarMessage } from '../components/common/CustomSnackbar';
import ImageCarousel from '../components/common/ImageCarousel';
import StoreAvatar from '../components/common/StoreAvatar';
import { useEffect, useState } from 'react';

const StoreDetail = () => {
  const { storeId: paramStoreId } = useParams();
  const { storeId: authStoreId } = useAuth();
  const theme = useTheme();
  
  // Use store ID from params if available, otherwise use from auth
  const currentStoreId = paramStoreId || authStoreId;

  const [snackbar, setSnackbar] = useState<SnackbarMessage>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Update localStorage when storeId from URL changes
  useEffect(() => {
    if (paramStoreId) {
      localStorage.setItem('storeId', paramStoreId);
    }
  }, [paramStoreId]);

  // Check for success message from store edit
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const updated = urlParams.get('updated');
    if (updated === 'true') {
      setSnackbar({
        open: true,
        message: 'Store details updated successfully!',
        severity: 'success',
        title: 'Success'
      });
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleShareClick = () => {
    const url = `${window.location.origin}/store/${currentStoreId}/details`;
    const storeName = store?.name || "Elite Barbershop";
    const storeDescription = store?.description || "Premium barbershop offering traditional and modern cuts with exceptional service.";
    
    if (navigator.share) {
      navigator.share({
        title: storeName,
        text: storeDescription,
        url: url,
      });
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(url).then(() => {
        setSnackbar({
          open: true,
          message: 'Store URL copied to clipboard!',
          severity: 'success',
        });
      });
    }
  };

  const { data: staffData, loading: staffLoading } = useQuery(GET_ALL_STAFF, {
    variables: { storeId: currentStoreId },
    skip: !currentStoreId,
  });

  const { data: servicesData, loading: servicesLoading } = useQuery(GET_ALL_SERVICES, {
    variables: { storeId: currentStoreId },
    skip: !currentStoreId,
  });

  const { data: ratingsData, loading: ratingsLoading } = useQuery(GET_ALL_RATINGS, {
    variables: { storeId: currentStoreId },
    skip: !currentStoreId,
  });

  const { data: storeData, loading: storeLoading } = useQuery(GET_STORE_BY_ID, {
    variables: { storeId: currentStoreId },
    skip: !currentStoreId,
  });

  if (staffLoading || servicesLoading || ratingsLoading || storeLoading) {
    return <LoadingSpinner message="Loading store details..." />;
  }

  const staff = staffData?.allStaff || [];
  const services = servicesData?.allServices || [];
  const ratings = ratingsData?.allRatings || [];
  const store = storeData?.storeDetails;

  const averageRating = ratings.length > 0 
    ? ratings.reduce((sum: number, rating: any) => sum + parseFloat(rating.rating), 0) / ratings.length 
    : 0;

  // Get carousel images
  const carouselImages = store?.images 
    ? store.images.filter((img: any) => img.imageType === 'carousel').map((img: any) => img.image)
    : [];

  // Get profile image (fallback to first profile image if avatar is not set)
  const profileImage = store?.images 
    ? store.images.find((img: any) => img.imageType === 'profile')
    : null;
  
  const displayAvatar = store?.avatar || profileImage?.image;

  const formatTime = (time: string) => {
    if (!time) return "N/A";
    // If time is in HH:MM format, convert to HH:MM AM/PM
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Function to check if store is currently open
  const isStoreOpen = () => {
    if (!store) return false;
    
    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[now.getDay()];
    const currentTime = now.getHours() * 60 + now.getMinutes(); // Convert to minutes
    
    // Get store hours for current day
    let startTime, endTime;
    if (['saturday', 'sunday'].includes(currentDay)) {
      startTime = store.weekendStartTime;
      endTime = store.weekendEndTime;
    } else {
      startTime = store.weekdayStartTime;
      endTime = store.weekdayEndTime;
    }
    
    if (!startTime || !endTime) return false;
    
    // Convert store hours to minutes
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);
    const storeStartTime = startHours * 60 + startMinutes;
    const storeEndTime = endHours * 60 + endMinutes;
    
    return currentTime >= storeStartTime && currentTime <= storeEndTime;
  };

  // Function to get next opening time
  const getNextOpeningTime = () => {
    if (!store) return null;
    
    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[now.getDay()];
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    // Check if store opens later today
    let todayStartTime, todayEndTime;
    if (['saturday', 'sunday'].includes(currentDay)) {
      todayStartTime = store.weekendStartTime;
      todayEndTime = store.weekendEndTime;
    } else {
      todayStartTime = store.weekdayStartTime;
      todayEndTime = store.weekdayEndTime;
    }
    
    if (todayStartTime && todayEndTime) {
      const [startHours, startMinutes] = todayStartTime.split(':').map(Number);
      const todayStartInMinutes = startHours * 60 + startMinutes;
      
      // If store opens later today
      if (currentTime < todayStartInMinutes) {
        return `Opens at ${formatTime(todayStartTime)}`;
      }
    }
    
    // Check next 7 days for opening
    for (let i = 1; i <= 7; i++) {
      const nextDate = new Date(now);
      nextDate.setDate(now.getDate() + i);
      const nextDay = dayNames[nextDate.getDay()];
      
      let nextStartTime;
      if (['saturday', 'sunday'].includes(nextDay)) {
        nextStartTime = store.weekendStartTime;
      } else {
        nextStartTime = store.weekdayStartTime;
      }
      
      if (nextStartTime) {
        const dayOfWeek = nextDay.charAt(0).toUpperCase() + nextDay.slice(1);
        if (i === 1) {
          return `Opens tomorrow at ${formatTime(nextStartTime)}`;
        } else {
          return `Opens ${dayOfWeek} at ${formatTime(nextStartTime)}`;
        }
      }
    }
    
    return 'Closed';
  };

  const storeStatus = isStoreOpen() ? 'Open Now' : getNextOpeningTime();

  // Store information from GraphQL data or fallback to mock data
  const storeInfo = store ? {
    name: store.name || "Elite Barbershop",
    description: store.description || "Premium barbershop offering traditional and modern cuts with exceptional service.",
    address: store.address || "123 Main Street, Downtown, City 12345",
    phone: store.phone || "+1 (555) 123-4567",
    email: store.email || "info@elitebarbershop.com",
    hours: {
      monday: `${formatTime(store.weekdayStartTime)} - ${formatTime(store.weekdayEndTime)}`,
      tuesday: `${formatTime(store.weekdayStartTime)} - ${formatTime(store.weekdayEndTime)}`,
      wednesday: `${formatTime(store.weekdayStartTime)} - ${formatTime(store.weekdayEndTime)}`,
      thursday: `${formatTime(store.weekdayStartTime)} - ${formatTime(store.weekdayEndTime)}`,
      friday: `${formatTime(store.weekdayStartTime)} - ${formatTime(store.weekdayEndTime)}`,
      saturday: `${formatTime(store.weekendStartTime)} - ${formatTime(store.weekendEndTime)}`,
      sunday: `${formatTime(store.weekendStartTime)} - ${formatTime(store.weekendEndTime)}`
    },
    image: "/images/barbershop.png"
  } : {
    name: "Elite Barbershop",
    description: "Premium barbershop offering traditional and modern cuts with exceptional service.",
    address: "123 Main Street, Downtown, City 12345",
    phone: "+1 (555) 123-4567",
    email: "info@elitebarbershop.com",
    hours: {
      monday: "9:00 AM - 8:00 PM",
      tuesday: "9:00 AM - 8:00 PM",
      wednesday: "9:00 AM - 8:00 PM",
      thursday: "9:00 AM - 8:00 PM",
      friday: "9:00 AM - 9:00 PM",
      saturday: "8:00 AM - 9:00 PM",
      sunday: "10:00 AM - 6:00 PM"
    },
    image: "/images/barbershop.png"
  };

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      maxWidth: '1200px', 
      mx: 'auto',
      minHeight: '100vh',
      bgcolor: 'background.default'
    }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 0 },
          mb: 3,
          pb: 2,
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Business sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 2 }} />
            <Box>
              <Typography variant="h4" component="h1" fontWeight={700} sx={{ mb: 0.5 }}>
                Store Details
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                Comprehensive information about your barbershop
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Share />}
              onClick={handleShareClick}
              sx={{ 
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Share
            </Button>
            <Button
              variant="contained"
              startIcon={<Edit />}
              component={Link}
              to={`/store/${currentStoreId}/edit`}
              sx={{ 
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: theme.shadows[2]
              }}
            >
              Edit Store
            </Button>
          </Box>
        </Box>



        <Grid container spacing={3}>
          {/* Store Images Carousel */}
          {carouselImages.length > 0 && (
            <Grid item xs={12}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Paper sx={{ 
                  p: { xs: 2, md: 3 },
                  borderRadius: 2,
                  boxShadow: theme.shadows[1],
                  bgcolor: 'background.paper'
                }}>
                  <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
                    Store Gallery
                  </Typography>
                  <ImageCarousel images={carouselImages} height={300} />
                </Paper>
              </motion.div>
            </Grid>
          )}

          {/* Store Overview */}
          <Grid item xs={12} md={8}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Paper sx={{ 
                p: { xs: 2, md: 3 },
                borderRadius: 2,
                boxShadow: theme.shadows[1],
                bgcolor: 'background.paper',
                mb: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                  <StoreAvatar
                    storeAvatar={displayAvatar}
                    storeName={storeInfo.name}
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      mr: 3,
                      bgcolor: theme.palette.primary.main,
                      boxShadow: theme.shadows[3]
                    }}
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Typography variant="h5" fontWeight={600}>
                        {storeInfo.name}
                      </Typography>
                      <Chip
                        label={store.isActive ? 'Active' : 'Inactive'}
                        color={store.isActive ? 'success' : 'default'}
                        variant="outlined"
                        size="small"
                        sx={{ 
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      />
                    </Box>
                    <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 2 }}>
                      {storeInfo.description}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        p: 1,
                        border: 1,
                        borderColor: 'warning.main',
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.warning.main, 0.05)
                      }}>
                        <Star sx={{ color: theme.palette.warning.main, mr: 0.5 }} />
                        <Typography variant="body2" fontWeight={600}>
                          {averageRating.toFixed(1)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                          ({ratings.length} reviews)
                        </Typography>
                      </Box>
                      <Chip 
                        label={storeStatus} 
                        color={isStoreOpen() ? "success" : "warning"} 
                        size="small"
                        sx={{ 
                          borderRadius: 1.5,
                          fontWeight: 600,
                          maxWidth: 200,
                          '& .MuiChip-label': {
                            fontSize: '0.75rem',
                            px: 1
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Contact Information */}
                <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
                  Contact Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ 
                      p: 2, 
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      bgcolor: 'background.default',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': { 
                        borderColor: theme.palette.primary.main,
                        boxShadow: theme.shadows[2]
                      }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <LocationOn sx={{ color: theme.palette.primary.main, mr: 1 }} />
                        <Typography variant="subtitle2" fontWeight={600}>
                          Address
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {storeInfo.address}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ 
                      p: 2, 
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      bgcolor: 'background.default',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': { 
                        borderColor: theme.palette.primary.main,
                        boxShadow: theme.shadows[2]
                      }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Phone sx={{ color: theme.palette.primary.main, mr: 1 }} />
                        <Typography variant="subtitle2" fontWeight={600}>
                          Phone
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {storeInfo.phone}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{
                      p: 2,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      bgcolor: 'background.default',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: theme.shadows[2]
                      }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Email sx={{ color: theme.palette.primary.main, mr: 1 }} />
                        <Typography variant="subtitle2" fontWeight={600}>
                          Email
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {storeInfo.email}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Social Media Icons */}
                  {(store?.instagramUrl || store?.facebookUrl || store?.whatsappUrl || store?.telegramUrl || store?.linkedinUrl) && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {[
                          { url: store?.instagramUrl, Icon: FaInstagram, label: 'Instagram' },
                          { url: store?.facebookUrl, Icon: FaFacebook, label: 'Facebook' },
                          { url: store?.whatsappUrl, Icon: FaWhatsapp, label: 'WhatsApp' },
                          { url: store?.telegramUrl, Icon: FaTelegram, label: 'Telegram' },
                          { url: store?.linkedinUrl, Icon: FaLinkedin, label: 'LinkedIn' },
                        ].filter(({ url }) => url).map(({ url, Icon, label }) => (
                          <Tooltip key={label} title={label}>
                            <IconButton
                              component="a"
                              href={url!}
                              target="_blank"
                              rel="noopener noreferrer"
                              size="small"
                              sx={{
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1.5,
                                bgcolor: 'background.default',
                                color: 'text.secondary',
                                transition: 'all 0.3s ease-in-out',
                                '&:hover': {
                                  borderColor: theme.palette.primary.main,
                                  color: theme.palette.primary.main,
                                  boxShadow: theme.shadows[2]
                                }
                              }}
                            >
                              <Icon size={18} />
                            </IconButton>
                          </Tooltip>
                        ))}
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* Operating Hours */}
              <Paper sx={{ 
                p: { xs: 2, md: 3 },
                borderRadius: 2,
                boxShadow: theme.shadows[1],
                bgcolor: 'background.paper'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Schedule sx={{ color: theme.palette.primary.main, mr: 1 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Operating Hours
                  </Typography>
                </Box>
                <Box sx={{ 
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  bgcolor: 'background.default',
                  overflow: 'hidden'
                }}>
                  {Object.entries(storeInfo.hours).map(([day, hours], index) => (
                    <Box 
                      key={day}
                      sx={{ 
                        p: 2, 
                        borderBottom: index < Object.entries(storeInfo.hours).length - 1 ? 1 : 0,
                        borderColor: 'divider',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': { 
                          bgcolor: alpha(theme.palette.primary.main, 0.05)
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 600, minWidth: 80 }}>
                          {day}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                          {hours}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </motion.div>
          </Grid>

          {/* Quick Stats */}
          <Grid item xs={12} md={4}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Paper sx={{ 
                p: { xs: 2, md: 3 },
                borderRadius: 2,
                boxShadow: theme.shadows[1],
                bgcolor: 'background.paper',
                mb: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Assessment sx={{ color: theme.palette.primary.main, mr: 1 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Performance Overview
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ 
                      p: 2, 
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      bgcolor: 'background.default',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': { 
                        borderColor: theme.palette.primary.main,
                        boxShadow: theme.shadows[2]
                      }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <People sx={{ color: theme.palette.primary.main, mr: 1 }} />
                          <Typography variant="subtitle2" fontWeight={600}>
                            Staff Members
                          </Typography>
                        </Box>
                        <Typography variant="h5" fontWeight={700} color="primary">
                          {staff.length}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ 
                      p: 2, 
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      bgcolor: 'background.default',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': { 
                        borderColor: theme.palette.secondary.main,
                        boxShadow: theme.shadows[2]
                      }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <RoomService sx={{ color: theme.palette.secondary.main, mr: 1 }} />
                          <Typography variant="subtitle2" fontWeight={600}>
                            Services Offered
                          </Typography>
                        </Box>
                        <Typography variant="h5" fontWeight={700} color="secondary">
                          {services.length}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ 
                      p: 2, 
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      bgcolor: 'background.default',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': { 
                        borderColor: theme.palette.warning.main,
                        boxShadow: theme.shadows[2]
                      }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Star sx={{ color: theme.palette.warning.main, mr: 1 }} />
                          <Typography variant="subtitle2" fontWeight={600}>
                            Average Rating
                          </Typography>
                        </Box>
                        <Typography variant="h5" fontWeight={700} sx={{ color: theme.palette.warning.main }}>
                          {averageRating.toFixed(1)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Quick Actions */}
              <Paper sx={{ 
                p: { xs: 2, md: 3 },
                borderRadius: 2,
                boxShadow: theme.shadows[1],
                bgcolor: 'background.paper'
              }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                  Quick Actions
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box 
                    sx={{ 
                      p: 2, 
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      bgcolor: 'background.default',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': { 
                        borderColor: theme.palette.primary.main,
                        boxShadow: theme.shadows[2]
                      },
                      textDecoration: 'none',
                      color: 'inherit'
                    }}
                    component={Link}
                    to={`/store/${currentStoreId}/appointments`}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CalendarMonth sx={{ color: theme.palette.primary.main, mr: 2 }} />
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          View Appointments
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Manage your bookings
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  <Box 
                    sx={{ 
                      p: 2, 
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      bgcolor: 'background.default',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': { 
                        borderColor: theme.palette.secondary.main,
                        boxShadow: theme.shadows[2]
                      },
                      textDecoration: 'none',
                      color: 'inherit'
                    }}
                    component={Link}
                    to={`/store/${currentStoreId}/staff`}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <People sx={{ color: theme.palette.secondary.main, mr: 2 }} />
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Manage Staff
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Add and organize staff
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  <Box 
                    sx={{ 
                      p: 2, 
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      bgcolor: 'background.default',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': { 
                        borderColor: theme.palette.success.main,
                        boxShadow: theme.shadows[2]
                      },
                      textDecoration: 'none',
                      color: 'inherit'
                    }}
                    component={Link}
                    to={`/store/${currentStoreId}/services`}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <RoomService sx={{ color: theme.palette.success.main, mr: 2 }} />
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Manage Services
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Configure your services
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>

        {/* Snackbar for notifications */}
        <CustomSnackbar
          snackbar={snackbar}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        />
      </motion.div>
    </Box>
  );
};

export default StoreDetail;
